import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, TrendingUp, TrendingDown, CheckCircle2, AlertTriangle, Landmark } from "lucide-react";

const fmt = (n) => (typeof n === "number" ? `$${Math.round(n).toLocaleString()}` : "—");
const fmtu = (n) => (typeof n === "number" ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");

/* Dir 146 COCKPIT FALLBACK — the rare dead-end escape hatch for a major line the engine could not ground on a
   cited band. Officer/admin either supplies a real comp (URL + unit price) or accepts-with-reason; recorded to
   pricing_sources (who/when/why) and clears the line's UNGROUNDED marker without a full re-price. */
function LineFallback({ dealId, line, idx, unit, qty, buildExt, bidTotal, onDone }) {
  const [mode, setMode] = useState(null);
  const [compUrl, setCompUrl] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [opRate, setOpRate] = useState("");
  const [opNote, setOpNote] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const u = unit || "unit";

  const submit = async () => {
    if (mode === "comp" && (!compUrl.trim() || !unitPrice)) { toast.error("Comp URL and unit price are required."); return; }
    if (mode === "operator" && (!opRate || parseFloat(opRate) <= 0)) { toast.error(`Enter your operator rate ($ / ${u}).`); return; }
    if (mode === "accept" && !reason.trim()) { toast.error("A reason is required."); return; }
    setSaving(true);
    try {
      let body;
      if (mode === "comp") body = { line, mode: "comp", comp_url: compUrl.trim(), unit_price: parseFloat(unitPrice) };
      else if (mode === "operator") body = { line, mode: "operator", unit_price: parseFloat(opRate), reason: opNote.trim() || undefined };
      else body = { line, mode: "accept", reason: reason.trim() };
      await api.post(`/bids/${dealId}/pricing/line-grounding/supply-comp`, body);
      toast.success(mode === "operator" ? "Operator estimate recorded — line grounded, price can go firm."
        : mode === "comp" ? "Comp recorded — line grounded." : "Accepted with reason — line grounded.");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record.");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-2 border-t border-red-200 pt-2" data-testid={`line-fallback-${idx}`}>
      {!mode && (
        <div className="space-y-1.5">
          <div className="text-[11px] text-red-700 font-semibold" data-testid={`line-fallback-prompt-${idx}`}>
            This line needs your operator estimate ($ / {u}) to go firm.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setMode("operator")} data-testid={`line-fallback-operator-btn-${idx}`}
              className="text-[11px] px-2.5 py-1 rounded bg-red-600 text-white font-semibold hover:bg-red-700 shadow-sm">
              Enter operator estimate ($ / {u})
            </button>
            <button onClick={() => setMode("comp")} data-testid={`line-fallback-comp-btn-${idx}`}
              className="text-[10px] px-2 py-0.5 rounded bg-white border border-red-300 text-red-700 hover:bg-red-100">Supply comp</button>
            <button onClick={() => setMode("accept")} data-testid={`line-fallback-accept-btn-${idx}`}
              className="text-[10px] px-2 py-0.5 rounded bg-white border border-red-300 text-red-700 hover:bg-red-100">Accept with reason</button>
          </div>
        </div>
      )}
      {mode === "operator" && (
        <div className="space-y-1">
          <input value={opRate} onChange={(e) => setOpRate(e.target.value)} placeholder={`Your operator rate ($ / ${u})`} type="number" autoFocus
            data-testid={`line-fallback-operator-rate-${idx}`} className="w-full text-[11px] border border-red-300 rounded px-2 py-1" />
          {(() => {
            const rate = parseFloat(opRate);
            if (!(rate > 0) || !(qty > 0)) return null;
            const newLine = rate * qty;
            const newBid = (typeof bidTotal === "number" && typeof buildExt === "number") ? (bidTotal - buildExt + newLine) : null;
            return (
              <div className="text-[11px] text-zinc-700 bg-white border border-red-200 rounded px-2 py-1" data-testid={`line-fallback-operator-preview-${idx}`}>
                ≈ {fmtu(rate)}/{u} × {qty.toLocaleString()} {u} = <b>{fmt(newLine)}</b> line total
                {newBid != null && <> → est. bid total <b>{fmt(newBid)}</b></>}
              </div>
            );
          })()}
          <input value={opNote} onChange={(e) => setOpNote(e.target.value)} placeholder="Optional note (crew, production rate, basis)"
            data-testid={`line-fallback-operator-note-${idx}`} className="w-full text-[11px] border border-red-200 rounded px-2 py-1" />
          <div className="flex gap-2">
            <button onClick={submit} disabled={saving} data-testid={`line-fallback-operator-save-${idx}`}
              className="text-[10px] px-2 py-1 rounded bg-red-600 text-white font-semibold disabled:opacity-50">{saving ? "Saving…" : "Ground with my estimate"}</button>
            <button onClick={() => setMode(null)} className="text-[10px] px-2 py-1 rounded border border-zinc-300">Cancel</button>
          </div>
        </div>
      )}
      {mode === "comp" && (
        <div className="space-y-1">
          <input value={compUrl} onChange={(e) => setCompUrl(e.target.value)} placeholder="Comparable source URL (fee schedule / award / published rate)"
            data-testid={`line-fallback-comp-url-${idx}`} className="w-full text-[11px] border border-red-200 rounded px-2 py-1" />
          <input value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} placeholder="Unit price ($ / unit)" type="number"
            data-testid={`line-fallback-comp-price-${idx}`} className="w-full text-[11px] border border-red-200 rounded px-2 py-1" />
          <div className="flex gap-2">
            <button onClick={submit} disabled={saving} data-testid={`line-fallback-comp-save-${idx}`}
              className="text-[10px] px-2 py-1 rounded bg-zinc-900 text-white disabled:opacity-50">{saving ? "Saving…" : "Record comp"}</button>
            <button onClick={() => setMode(null)} className="text-[10px] px-2 py-1 rounded border border-zinc-300">Cancel</button>
          </div>
        </div>
      )}
      {mode === "accept" && (
        <div className="space-y-1">
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
            placeholder="Reason for accepting the build price without a cited comp (recorded who/when/why)"
            data-testid={`line-fallback-accept-reason-${idx}`} className="w-full text-[11px] border border-red-200 rounded px-2 py-1" />
          <div className="flex gap-2">
            <button onClick={submit} disabled={saving} data-testid={`line-fallback-accept-save-${idx}`}
              className="text-[10px] px-2 py-1 rounded bg-zinc-900 text-white disabled:opacity-50">{saving ? "Saving…" : "Accept with reason"}</button>
            <button onClick={() => setMode(null)} className="text-[10px] px-2 py-1 rounded border border-zinc-300">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* Dir 145 — GOVERNMENT PRICING GROUNDING: shows USAspending.gov regional/historical award comparables, the
   bid-total band check, and any ungrounded (opaque) cost lines. Never invents numbers. */
export function PricingGroundingCard({ dealId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/bids/${dealId}/pricing/grounding`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grounding check failed.");
    } finally { setLoading(false); }
  };

  const g = data?.grounding || {};
  const band = g.band || {};
  const stats = data?.comparables?.stats || {};
  const status = band.status || "";
  const good = status === "IN_BAND";
  const under = status === "UNDERPRICED_VS_REGIONAL";

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="pricing-grounding-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Landmark className="h-4 w-4 text-zinc-700" />
          <h3 className="text-sm font-bold text-zinc-900">Pricing Grounding — Government Data</h3>
        </div>
        <button onClick={check} disabled={loading} data-testid="pricing-grounding-check-btn"
          className="text-[11px] bg-zinc-900 text-white px-3 py-1.5 rounded hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-1">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          {data ? "Re-check" : "Check grounding"}
        </button>
      </div>

      {!data && (
        <p className="text-[12px] text-zinc-500 mt-2">
          Compares this bid against real USAspending.gov prime awards (by NAICS + place of performance) and
          checks every cost line is traceable. Click to run.
        </p>
      )}

      {data && (
        <div className="mt-3 space-y-3">
          <div className="text-[11px] text-zinc-500">
            NAICS <span className="font-mono text-zinc-800">{data.naics || "—"}</span> · {data.state || "—"}
            {data.county ? `, ${data.county} County` : ""} · {data.comparables?.count || 0} comparable awards
          </div>

          {data.comparables?.escalation?.applied && (
            <div className="text-[10px] text-zinc-500" data-testid="pricing-grounding-escalation">
              Comparables escalated to {data.comparables.escalation.to_year} dollars via BLS PPI —{" "}
              {data.comparables.escalation.series_label}
            </div>
          )}

          <div className={`rounded-lg p-3 border ${good ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}
            data-testid="pricing-grounding-band">
            <div className="flex items-center gap-2 mb-1">
              {good ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                : under ? <TrendingDown className="h-4 w-4 text-amber-600" />
                : <TrendingUp className="h-4 w-4 text-amber-600" />}
              <span className={`text-[12px] font-bold ${good ? "text-emerald-700" : "text-amber-700"}`}>
                {status ? status.replace(/_/g, " ") : "No band data"}
              </span>
            </div>
            {band.checked && (
              <div className="grid grid-cols-3 gap-2 text-[11px] text-zinc-700">
                <div><div className="text-zinc-400">Bid total</div><div className="font-semibold">{fmt(band.bid_total)}</div></div>
                <div><div className="text-zinc-400">Regional median</div><div className="font-semibold">{fmt(band.regional_median)}</div></div>
                <div><div className="text-zinc-400">Ratio</div><div className="font-semibold">{band.ratio}×</div></div>
              </div>
            )}
          </div>

          {stats.median != null && (
            <div className="grid grid-cols-4 gap-2 text-[11px] text-zinc-700" data-testid="pricing-grounding-stats">
              {["min", "p25", "median", "p75"].map((k) => (
                <div key={k} className="rounded bg-zinc-50 border border-zinc-100 p-2">
                  <div className="text-zinc-400 uppercase">{k}</div>
                  <div className="font-semibold">{fmt(stats[k])}</div>
                </div>
              ))}
            </div>
          )}

          {(g.line_gaps || []).length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="pricing-grounding-gaps">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <span className="text-[12px] font-bold text-red-700">
                  {g.line_gaps.length} cost line{g.line_gaps.length > 1 ? "s" : ""} not fully grounded
                </span>
              </div>
              <ul className="text-[11px] text-red-800 space-y-0.5 max-h-32 overflow-auto">
                {g.line_gaps.slice(0, 8).map((gap) => (
                  <li key={gap.line}>Line {gap.line} ({fmt(gap.amount)}) — missing: {gap.missing.join(", ")}</li>
                ))}
              </ul>
              <div className="text-[10px] text-red-500 mt-1">Each line must carry a description, quantity, unit cost and basis — no opaque lump sums.</div>
            </div>
          )}

          {data.wage_determination?.regime && (
            <div className="text-[11px] text-zinc-600" data-testid="pricing-grounding-wage">
              <span className="font-semibold">Wage determination:</span>{" "}
              {String(data.wage_determination.regime).toUpperCase()}
              {data.wage_determination.wd_number ? ` · ${data.wage_determination.wd_number}` : ""}
              {data.wage_determination.revision != null ? ` rev ${data.wage_determination.revision}` : ""}
              {data.wage_determination.occupation_rates ? ` · ${data.wage_determination.occupation_rates} rates from sam.gov` : ""}
              {data.wage_determination.grounded ? " ✓" : " — not yet grounded"}
            </div>
          )}

          {(data.line_grounding?.line_bands || []).length > 0 && (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3" data-testid="line-grounding-evidence">
              <div className="text-[12px] font-bold text-zinc-800 mb-2">
                Line-level comp grounding <span className="font-normal text-zinc-400">(Dir 146 — build proposes, cited band disposes)</span>
              </div>
              <div className="space-y-2">
                {data.line_grounding.line_bands.map((lb, i) => {
                  const up = lb.status === "grounded_up";
                  const un = lb.status === "UNGROUNDED";
                  const above = lb.status === "above_market_band";
                  const mismatch = lb.status === "band-unit-mismatch";
                  const opc = lb.status === "operator_comp" || lb.status === "operator_accepted" || lb.status === "operator_estimate";
                  const needsFallback = un || mismatch;
                  const color = needsFallback ? "border-red-200 bg-red-50" : up ? "border-emerald-200 bg-emerald-50"
                    : above ? "border-amber-200 bg-amber-50" : opc ? "border-sky-200 bg-sky-50" : "border-zinc-200 bg-white";
                  const comp = (lb.comps || [])[0];
                  return (
                    <div key={i} className={`rounded border p-2 ${color}`} data-testid={`line-band-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-medium text-zinc-800 truncate">{lb.line}</span>
                        <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0 ${
                          needsFallback ? "bg-red-100 text-red-700" : up ? "bg-emerald-100 text-emerald-700"
                          : above ? "bg-amber-100 text-amber-700" : opc ? "bg-sky-100 text-sky-700" : "bg-zinc-100 text-zinc-600"}`}>
                          {String(lb.status || "").replace(/_/g, " ")}
                        </span>
                      </div>
                      {up && (
                        <div className="text-[10px] text-zinc-600 mt-1">
                          build <span className="font-mono">{fmtu(lb.build_unit_rate)}/{lb.band_unit}</span> → cited floor{" "}
                          <span className="font-mono font-semibold">{fmtu(lb.band_floor)}/{lb.band_unit}</span>
                          {lb.escalation?.factor ? <> · escalated ×{lb.escalation.factor} ({lb.escalation.series} {lb.escalation.from_year}→{lb.escalation.to_year})</> : null}
                          {lb.grounded_extended ? <> · line → <span className="font-semibold">{fmt(lb.grounded_extended)}</span></> : null}
                        </div>
                      )}
                      {needsFallback && (
                        <div className="text-[10px] text-red-700 mt-1">
                          {lb.reason || "No cited comparable band and only a labor/wage build — needs an operator comp or accept-with-reason."}
                        </div>
                      )}
                      {opc && lb.operator && (
                        <div className="text-[10px] text-sky-800 mt-1" data-testid={`line-operator-note-${i}`}>
                          {lb.operator.mode === "comp"
                            ? <>Grounded by operator comp: <span className="font-semibold">{fmtu(lb.operator.unit_price)}/{lb.band_unit || lb.unit}</span>{lb.operator.comp_url ? <> — <a className="underline" href={lb.operator.comp_url} target="_blank" rel="noreferrer">source</a></> : null} · {lb.operator.by}</>
                            : lb.operator.mode === "operator"
                            ? <>Grounded by operator estimate: <span className="font-semibold">{fmtu(lb.operator.unit_price)}/{lb.band_unit || lb.unit}</span>{lb.operator.reason ? <> — {lb.operator.reason}</> : null} · {lb.operator.by}</>
                            : <>Accepted with reason by {lb.operator.by}: {lb.operator.reason}</>}
                        </div>
                      )}
                      {comp && (
                        <div className="text-[9px] text-zinc-400 mt-1 truncate">
                          Comp: {comp.project} ({comp.year}) — {comp.unit_price != null ? `${comp.unit_price} ${comp.unit || ""}` : comp.total ? fmt(comp.total) : comp.range ? `$${comp.range[0]}-${comp.range[1]} ${comp.unit || ""}` : ""} · {comp.source}
                        </div>
                      )}
                      {needsFallback && <LineFallback dealId={dealId} line={lb.line} idx={i} unit={lb.unit}
                        qty={lb.quantity} buildExt={lb.build_extended} bidTotal={data.line_grounding?.bid_total} onDone={check} />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PricingGroundingCard;
